// Style and grade label per curriculum, keyed by DB-side value (lowercased).
// We deliberately don't store an `abbreviation` or `fullName` here — the DB
// curriculum string IS the canonical name (e.g. "CC", "AP", "Athena-CC") and
// is shown verbatim in the UI.
export const curriculumConfig = {
  cc: {
    bgColor: "bg-amber-100",
    cardBgColor: "bg-amber-50",
    color: "text-amber-700",
    borderColor: "border-amber-200",
    gradeLabel: "Grade",
  },
  teks: {
    bgColor: "bg-sky-100",
    cardBgColor: "bg-sky-50",
    color: "text-sky-700",
    borderColor: "border-sky-200",
    gradeLabel: "Grade",
  },
  ap: {
    bgColor: "bg-violet-100",
    cardBgColor: "bg-violet-50",
    color: "text-violet-700",
    borderColor: "border-violet-200",
    gradeLabel: "Unit",
  },
  sat: {
    bgColor: "bg-emerald-100",
    cardBgColor: "bg-emerald-50",
    color: "text-emerald-700",
    borderColor: "border-emerald-200",
    gradeLabel: "Unit",
  },
  "athena-cc": {
    bgColor: "bg-rose-100",
    cardBgColor: "bg-rose-50",
    color: "text-rose-700",
    borderColor: "border-rose-200",
    gradeLabel: "Grade",
  },
};

const DEFAULT_STYLE = {
  bgColor: "bg-gray-100",
  cardBgColor: "bg-gray-50",
  color: "text-gray-700",
  borderColor: "border-gray-200",
  gradeLabel: "Grade",
};

// Get curriculum display info. `abbreviation` and `fullName` both echo the
// DB-side curriculum value verbatim — there is no separate display name.
export const getCurriculumConfig = (dbName) => {
  const display = dbName || "-";
  const style =
    (dbName && curriculumConfig[dbName.toLowerCase()]) || DEFAULT_STYLE;
  return {
    abbreviation: display,
    fullName: display,
    ...style,
  };
};

// Get the label for the "grade" field based on curriculum (e.g., "Grade" or "Unit")
export const getGradeLabel = (curriculum) => {
  return getCurriculumConfig(curriculum).gradeLabel;
};

// Request category configuration
export const REQUEST_CATEGORY_CONFIG = {
  question: {
    label: "Questions",
    shortLabel: "Q",
    bgColor: "bg-blue-100",
    color: "text-blue-700",
    borderColor: "border-blue-200",
  },
  article: {
    label: "Articles",
    shortLabel: "A",
    bgColor: "bg-emerald-100",
    color: "text-emerald-700",
    borderColor: "border-emerald-200",
  },
};

export const getRequestCategoryConfig = (category) => {
  if (!category) {
    return REQUEST_CATEGORY_CONFIG.question;
  }
  return (
    REQUEST_CATEGORY_CONFIG[category.toLowerCase()] || {
      label: category,
      shortLabel: category.slice(0, 1).toUpperCase(),
      bgColor: "bg-gray-100",
      color: "text-gray-700",
      borderColor: "border-gray-200",
    }
  );
};

export const getRequestCategoryLabel = (category) => {
  return getRequestCategoryConfig(category).label;
};

// Request modifier flags configuration
export const REQUEST_MODIFIER_CONFIG = {
  interest: {
    label: "Interest",
    shortLabel: "I",
    bgColor: "bg-amber-100",
    color: "text-amber-700",
  },
  stimulus: {
    label: "Stimulus",
    shortLabel: "S",
    bgColor: "bg-rose-100",
    color: "text-rose-700",
  },
};

/**
 * Build an array of modifier badges to display for a run's filters.
 * Returns objects with { key, label, shortLabel, bgColor, color }.
 */
export const getModifierBadges = (filters) => {
  if (!filters) {
    return [];
  }
  const badges = [];
  if (filters.has_interests) {
    badges.push({ key: "interest", ...REQUEST_MODIFIER_CONFIG.interest });
  }
  if (filters.has_stimulus) {
    badges.push({ key: "stimulus", ...REQUEST_MODIFIER_CONFIG.stimulus });
  }
  return badges;
};

// Display names for question/content types
const TYPE_DISPLAY_NAMES = {
  mcq: "MCQ",
  mcq_set: "MCQ Set",
  saq: "SAQ",
  leq: "LEQ",
  dbq: "DBQ",
  frq_short: "FRQ Short",
  frq_long: "FRQ Long",
  article: "Article",
};

// Get a human-readable label for a question type
export const getTypeDisplayName = (type) => {
  if (!type) {
    return "Unknown";
  }
  return TYPE_DISPLAY_NAMES[type.toLowerCase()] || type.toUpperCase();
};
